import { useGoogleOneTapLogin } from '@react-oauth/google';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthStore } from '../store/authStore';

const ONE_TAP_DISMISSAL_KEY = 'googleOneTapDismissedAt';
const DISMISSAL_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Module-level helpers — try/catch outside component so React Compiler can optimise GoogleOneTap.

function readDismissedRecently(): boolean {
  try {
    const dismissedAt = localStorage.getItem(ONE_TAP_DISMISSAL_KEY);
    if (!dismissedAt) return false;
    return Date.now() - parseInt(dismissedAt, 10) < DISMISSAL_DURATION_MS;
  } catch {
    return false;
  }
}

function saveDismissalTimestamp(): void {
  try {
    localStorage.setItem(ONE_TAP_DISMISSAL_KEY, Date.now().toString());
  } catch {
    // Ignore localStorage errors (private browsing, storage quota, etc.)
  }
}

async function tryOneTapSignIn(
  googleSignIn: (args: { idToken: string }) => Promise<{ isNewUser: boolean }>,
  credential: string,
  onSuccess: (isNewUser: boolean) => void,
  clearError: () => void
): Promise<void> {
  clearError();
  try {
    const { isNewUser } = await googleSignIn({ idToken: credential });
    localStorage.removeItem(ONE_TAP_DISMISSAL_KEY);
    onSuccess(isNewUser);
  } catch {
    // Error set in auth store
  }
}

/**
 * Google One Tap sign-in prompt.
 * Displays automatically on page load for unauthenticated users.
 * Disabled on the home page (where MagicLinkAuth already shows Google button).
 * Suppressed for 24h after user dismisses the prompt.
 */
export function GoogleOneTap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, googleSignIn, clearError } = useAuthStore();

  // Disable on home page (has its own Google button) and auth-related pages
  const isAuthPage =
    location.pathname === '/' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname.startsWith('/onboarding');

  // useState initializer runs exactly once on mount — same semantics as useMemo([])
  // but without a try/catch in the component body (Compiler blocker).
  const [wasDismissedRecently] = useState(readDismissedRecently);

  const handleSuccess = (response: { credential?: string }) => {
    if (!response.credential) return;
    tryOneTapSignIn(
      googleSignIn,
      response.credential,
      (isNewUser) => navigate(isNewUser ? '/onboarding/welcome' : '/stories'),
      clearError
    );
  };

  const handlePromptMoment = (notification: {
    isSkippedMoment: () => boolean;
    isDismissedMoment: () => boolean;
    getMomentType: () => string;
  }) => {
    const type = notification.getMomentType();
    if (
      notification.isSkippedMoment() ||
      notification.isDismissedMoment() ||
      type === 'skipped' ||
      type === 'dismissed'
    ) {
      saveDismissalTimestamp();
    }
  };

  useGoogleOneTapLogin({
    onSuccess: handleSuccess,
    onError: () => {
      // One Tap dismissed or failed — no action needed
    },
    promptMomentNotification: handlePromptMoment,
    disabled:
      isAuthenticated ||
      isAuthPage ||
      wasDismissedRecently ||
      !import.meta.env.VITE_GOOGLE_CLIENT_ID,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true, // Use Federated Credential Management API on supported browsers (Chrome 117+)
  });

  return null;
}
